/**
 * View-setting persistence, keyed per project.
 *
 * Two-page view, split view and the thumbnail pane each lived in a single flat
 * localStorage key, so they were one value shared by every project: turn on
 * two-page view for one script, open another, and that one opened in two-page
 * view too. Exactly the bug `page-scale` had before lib/page-scale-storage.js,
 * and this mirrors that fix deliberately.
 *
 * localStorage and NOT the user document, for the same reason as zoom: how a
 * script is laid out on screen is a property of the screen you are sitting at.
 * A laptop and a 27" monitor want different answers, and syncing the choice
 * across devices would guarantee it is wrong on one of them.
 *
 * NOTE FOR ANYONE EXTENDING THIS: storage is only half of the fix. The Vuex
 * state fields (state.pageType, state.splitView, ...) survive a project switch
 * in memory and win over storage in the getters, so a new setting must ALSO be
 * re-seeded from here in palette/index.vue's mounted() hook. Without that, per
 * project keys are written correctly and the previous project's value is still
 * what you see.
 */

// flat key = the pre-migration name, still read as a fallback so nobody's
// current setting resets the first time they open a project after this ships.
const FLAT_KEYS = {
    pageType: 'page-type-selected',
    thumbnailView: 'thumbnail-selected',
    splitView: 'split-view-selected',
    splitPageSync: 'split-page-sync',
    splitRatio: 'split-ratio'
};

export function viewSettingKey(name, projectId) {
    const flat = FLAT_KEYS[name];
    if (!flat) {
        return null;
    }
    return projectId ? `${flat}:${projectId}` : flat;
}

/**
 * Per-project value first, then the pre-migration flat key, then null so the
 * caller applies its own default.
 *
 * The flat-key fallback is READ-only and never written back, so it is a frozen
 * legacy value rather than a live shared one — which is what makes this fix the
 * fix. Project B can inherit what was in the flat key BEFORE the migration, but
 * it can never pick up a change made to project A afterwards.
 */
export function readViewSetting(name, projectId) {
    const flat = FLAT_KEYS[name];
    if (!flat) {
        return null;
    }
    try {
        if (projectId) {
            const scoped = localStorage.getItem(viewSettingKey(name, projectId));
            if (scoped !== null && scoped !== '') {
                return scoped;
            }
        }
        const legacy = localStorage.getItem(flat);
        return legacy === '' ? null : legacy;
    } catch (e) {
        // Private browsing / storage disabled: the caller's default applies.
        return null;
    }
}

export function writeViewSetting(name, projectId, value) {
    const key = viewSettingKey(name, projectId);
    if (!key) {
        return;
    }
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        // Nothing to do — the setting simply will not persist for this session.
    }
}

export default { viewSettingKey, readViewSetting, writeViewSetting };


// WEBPACK FOOTER //
// ./src/lib/view-mode-storage.js